'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2ComparisonFileVersions, PrV2FileVisibility, PrV2VersionedFile } from '@/lib/pr-v2/file-visibility-storage';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getPrV2ComparisonFileVersions, getPrV2FileVersion, hasPrV2FileChangedSince, isPrV2FileVersionCurrent, readPrV2FileVisibility, repinPrV2FileVersion, subscribeToPrV2FileVisibilityChanges, writePrV2FileVisibility } from '@/lib/pr-v2/file-visibility-storage';
const EMPTY_DEFAULT_COLLAPSED_FILENAMES: string[] = [];
const EMPTY_FILENAMES: ReadonlySet<string> = new Set();
type UsePrV2FileVisibilityStateParams = {
  owner: string;
  repo: string;
  number: string;
  /** Head of the comparison on screen. */
  headSha: string | null;
  /**
   * Whether the comparison ends at the PR's latest head. On an older version, a file viewed at a
   * later one differs without having changed since, so no file counts as changed since last view.
   */
  isLatestHead: boolean;
  /** The comparison's files, whose blob SHAs are the versions marks are pinned to. */
  files: PrV2VersionedFile[];
  defaultCollapsedFilenames?: string[];
};

/** Path → the version of the file a mark was made on. */
type FileVersionMarks = ReadonlyMap<string, string>;
type FileVisibilityState = {
  viewed: FileVersionMarks;
  userCollapsed: FileVersionMarks;
  userExpanded: FileVersionMarks;
};
type FileVisibilityList = keyof FileVisibilityState;
const FILE_VISIBILITY_LISTS: FileVisibilityList[] = ['viewed', 'userCollapsed', 'userExpanded'];
type FileVisibilityAction = {
  type: 'hydrateFromStorage';
  marks: FileVisibilityState;
} | {
  type: 'mergeFromAnotherTab';
  incoming: FileVisibilityState;
  /** The marks this tab last read from or saved to storage. */
  synced: FileVisibilityState;
} | {
  type: 'toggleViewed';
  filename: string;
  versions: PrV2ComparisonFileVersions;
} | {
  type: 'setFilesViewed';
  filenames: string[];
  viewed: boolean;
  versions: PrV2ComparisonFileVersions;
} | {
  type: 'setFilesCollapsed';
  filenames: string[];
  collapsed: boolean;
  versions: PrV2ComparisonFileVersions;
} | {
  type: 'pinHeadMarksToBlobs';
  versions: PrV2ComparisonFileVersions;
};
const INITIAL_FILE_VISIBILITY_STATE: FileVisibilityState = {
  viewed: new Map(),
  userCollapsed: new Map(),
  userExpanded: new Map()
};
function areMarksEqual(left: FileVersionMarks, right: FileVersionMarks): boolean {
  if (left === right) {
    return true;
  }
  if (left.size !== right.size) {
    return false;
  }
  for (const [filename, version] of left) {
    if (right.get(filename) !== version) {
      return false;
    }
  }
  return true;
}

// Debounce localStorage writes to avoid rapid update bursts while users navigate the diff.
const STORAGE_WRITE_DEBOUNCE_MS = 120;
type MutableFileVisibilityState = Record<FileVisibilityList, Map<string, string>>;
function copyState(state: FileVisibilityState): MutableFileVisibilityState {
  return {
    viewed: new Map(state.viewed),
    userCollapsed: new Map(state.userCollapsed),
    userExpanded: new Map(state.userExpanded)
  };
}

/**
 * Keeps `current` and each of its unchanged lists, so an action that changes nothing renders
 * nothing and a collapse toggle leaves the viewed set's identity alone.
 */
function toState(next: FileVisibilityState, current: FileVisibilityState): FileVisibilityState {
  const viewed = areMarksEqual(next.viewed, current.viewed) ? current.viewed : next.viewed;
  const userCollapsed = areMarksEqual(next.userCollapsed, current.userCollapsed) ? current.userCollapsed : next.userCollapsed;
  const userExpanded = areMarksEqual(next.userExpanded, current.userExpanded) ? current.userExpanded : next.userExpanded;
  if (viewed === current.viewed && userCollapsed === current.userCollapsed && userExpanded === current.userExpanded) {
    return current;
  }
  return {
    viewed,
    userCollapsed,
    userExpanded
  };
}
function markViewed(next: MutableFileVisibilityState, filename: string, version: string, viewed: boolean) {
  if (viewed) {
    next.viewed.set(filename, version);
    next.userCollapsed.set(filename, version);
    next.userExpanded.delete(filename);
    return;
  }
  next.viewed.delete(filename);
  next.userCollapsed.delete(filename);
  next.userExpanded.set(filename, version);
}
function markCollapsed(next: MutableFileVisibilityState, filename: string, version: string, collapsed: boolean) {
  if (collapsed) {
    next.userCollapsed.set(filename, version);
    next.userExpanded.delete(filename);
    return;
  }
  next.userCollapsed.delete(filename);
  next.userExpanded.set(filename, version);
}
function fileVisibilityReducer(state: FileVisibilityState, action: FileVisibilityAction): FileVisibilityState {
  switch (action.type) {
    case 'hydrateFromStorage':
      return toState(action.marks, state);
    case 'mergeFromAnotherTab':
      {
        // Take the other tab's marks, except on files this tab changed since its last sync: a
        // pending debounced save is about to write those.
        const next = copyState(action.incoming);
        for (const list of FILE_VISIBILITY_LISTS) {
          const local = state[list];
          const synced = action.synced[list];
          for (const filename of new Set([...local.keys(), ...synced.keys()])) {
            const version = local.get(filename);
            if (version === synced.get(filename)) {
              continue;
            }
            if (version === undefined) {
              next[list].delete(filename);
            } else {
              next[list].set(filename, version);
            }
          }
        }
        return toState(next, state);
      }
    case 'toggleViewed':
      {
        const version = getPrV2FileVersion(action.versions, action.filename);
        if (!version) {
          return state;
        }
        const viewedVersion = state.viewed.get(action.filename);
        // A file viewed at another version shows unviewed, so toggling marks it viewed again.
        const isViewed = viewedVersion !== undefined && isPrV2FileVersionCurrent(action.versions, action.filename, viewedVersion);
        const next = copyState(state);
        markViewed(next, action.filename, version, !isViewed);
        return toState(next, state);
      }
    case 'setFilesViewed':
      {
        const next = copyState(state);
        for (const filename of action.filenames) {
          const version = filename ? getPrV2FileVersion(action.versions, filename) : null;
          if (version) {
            markViewed(next, filename, version, action.viewed);
          }
        }
        return toState(next, state);
      }
    case 'setFilesCollapsed':
      {
        const next = copyState(state);
        for (const filename of action.filenames) {
          const version = filename ? getPrV2FileVersion(action.versions, filename) : null;
          if (version) {
            markCollapsed(next, filename, version, action.collapsed);
          }
        }
        return toState(next, state);
      }
    case 'pinHeadMarksToBlobs':
      {
        const next = copyState(state);
        for (const list of FILE_VISIBILITY_LISTS) {
          for (const [filename, version] of next[list]) {
            next[list].set(filename, repinPrV2FileVersion(action.versions, filename, version));
          }
        }
        return toState(next, state);
      }
    default:
      {
        const exhaustiveAction: never = action;
        void exhaustiveAction;
        return state;
      }
  }
}
function fromStoredVisibility(visibility: PrV2FileVisibility): FileVisibilityState {
  return {
    viewed: new Map(Object.entries(visibility.viewed)),
    userCollapsed: new Map(Object.entries(visibility.userCollapsed)),
    userExpanded: new Map(Object.entries(visibility.userExpanded))
  };
}
function toStoredVisibility(state: FileVisibilityState): PrV2FileVisibility {
  return {
    viewed: Object.fromEntries(state.viewed),
    userCollapsed: Object.fromEntries(state.userCollapsed),
    userExpanded: Object.fromEntries(state.userExpanded)
  };
}

/** Files whose mark applies to the version the comparison shows. */
function getCurrentlyMarkedFilenames(marks: FileVersionMarks, versions: PrV2ComparisonFileVersions): Set<string> {
  const filenames = new Set<string>();
  for (const [filename, version] of marks) {
    if (isPrV2FileVersionCurrent(versions, filename, version)) {
      filenames.add(filename);
    }
  }
  return filenames;
}

/**
 * Viewed, collapsed and expanded files of one pull request. Each mark is pinned to the version of
 * the file it was made on and applies only while the comparison shows that version, so a push
 * clears the marks on the files it changed and keeps the rest.
 */
export function usePrV2FileVisibilityState(t0) {
  const $ = _c(60);
  const {
    owner,
    repo,
    number,
    headSha,
    isLatestHead,
    files,
    defaultCollapsedFilenames: t1
  } = t0;
  const defaultCollapsedFilenames = t1 === undefined ? EMPTY_DEFAULT_COLLAPSED_FILENAMES : t1;
  const [state, dispatch] = useReducer(fileVisibilityReducer, INITIAL_FILE_VISIBILITY_STATE);
  const [initialDefaultCollapsedFilenames, setInitialDefaultCollapsedFilenames] = useState(null);
  if (initialDefaultCollapsedFilenames === null && defaultCollapsedFilenames.length > 0) {
    setInitialDefaultCollapsedFilenames([...defaultCollapsedFilenames]);
  }
  let t2;
  if ($[0] !== files || $[1] !== headSha) {
    t2 = getPrV2ComparisonFileVersions(files, headSha);
    $[0] = files;
    $[1] = headSha;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const versions = t2;
  const syncedMarksRef = useRef(INITIAL_FILE_VISIBILITY_STATE);
  const pendingSaveRef = useRef(null);
  const hasUnsavedUserChangesRef = useRef(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = action => {
      hasUnsavedUserChangesRef.current = true;
      dispatch(action);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dispatchUserChange = t3;
  let t4;
  let t5;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t4 = () => {
      const readMarks = () => fromStoredVisibility(readPrV2FileVisibility({
        owner,
        repo,
        number
      }));
      const marks = readMarks();
      syncedMarksRef.current = marks;
      dispatch({
        type: "hydrateFromStorage",
        marks
      });
      return subscribeToPrV2FileVisibilityChanges(() => {
        const incoming = readMarks();
        dispatch({
          type: "mergeFromAnotherTab",
          incoming,
          synced: syncedMarksRef.current
        });
        syncedMarksRef.current = incoming;
      });
    };
    t5 = [owner, repo, number];
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== versions) {
    t6 = () => {
      dispatch({
        type: "pinHeadMarksToBlobs",
        versions
      });
    };
    t7 = [versions];
    $[9] = versions;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] !== number || $[13] !== owner || $[14] !== repo || $[15] !== state) {
    t8 = () => {
      const save = () => {
        pendingSaveRef.current = null;
        hasUnsavedUserChangesRef.current = false;
        writePrV2FileVisibility({
          owner,
          repo,
          number
        }, toStoredVisibility(state));
        syncedMarksRef.current = state;
      };
      pendingSaveRef.current = save;
      const timeoutId = window.setTimeout(save, STORAGE_WRITE_DEBOUNCE_MS);
      return () => {
        window.clearTimeout(timeoutId);
      };
    };
    t9 = [owner, repo, number, state];
    $[12] = number;
    $[13] = owner;
    $[14] = repo;
    $[15] = state;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => () => {
      if (hasUnsavedUserChangesRef.current) {
        pendingSaveRef.current?.();
      }
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== number || $[20] !== owner || $[21] !== repo) {
    t11 = [owner, repo, number];
    $[19] = number;
    $[20] = owner;
    $[21] = repo;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  if ($[23] !== state.viewed || $[24] !== versions) {
    t12 = getCurrentlyMarkedFilenames(state.viewed, versions);
    $[23] = state.viewed;
    $[24] = versions;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const viewedFiles = t12;
  let t13;
  bb0: {
    if (!isLatestHead) {
      t13 = EMPTY_FILENAMES;
      break bb0;
    }
    let filenames;
    if ($[26] !== state.viewed || $[27] !== versions) {
      filenames = new Set();
      for (const [filename, version] of state.viewed) {
        if (hasPrV2FileChangedSince(versions, filename, version)) {
          filenames.add(filename);
        }
      }
      $[26] = state.viewed;
      $[27] = versions;
      $[28] = filenames;
    } else {
      filenames = $[28];
    }
    t13 = filenames;
  }
  const changedSinceLastViewFiles = t13;
  const resolvedDefaultCollapsedFilenames = initialDefaultCollapsedFilenames ?? defaultCollapsedFilenames;
  let next;
  if ($[29] !== resolvedDefaultCollapsedFilenames || $[30] !== state.userCollapsed || $[31] !== state.userExpanded || $[32] !== versions) {
    next = new Set(resolvedDefaultCollapsedFilenames);
    getCurrentlyMarkedFilenames(state.userCollapsed, versions).forEach(filename_0 => {
      next.add(filename_0);
    });
    getCurrentlyMarkedFilenames(state.userExpanded, versions).forEach(filename_1 => {
      next.delete(filename_1);
    });
    $[29] = resolvedDefaultCollapsedFilenames;
    $[30] = state.userCollapsed;
    $[31] = state.userExpanded;
    $[32] = versions;
    $[33] = next;
  } else {
    next = $[33];
  }
  const collapsedFiles = next;
  let t14;
  if ($[34] !== viewedFiles) {
    t14 = filename_2 => viewedFiles.has(filename_2);
    $[34] = viewedFiles;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const isViewed = t14;
  let t15;
  if ($[36] !== collapsedFiles) {
    t15 = filename_3 => collapsedFiles.has(filename_3);
    $[36] = collapsedFiles;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const isCollapsed = t15;
  let t16;
  if ($[38] !== versions) {
    t16 = filename_4 => {
      if (!filename_4) {
        return;
      }
      dispatchUserChange({
        type: "toggleViewed",
        filename: filename_4,
        versions
      });
    };
    $[38] = versions;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const toggleViewed = t16;
  let t17;
  if ($[40] !== versions) {
    t17 = (filenames_0, viewed) => {
      if (filenames_0.length === 0) {
        return;
      }
      dispatchUserChange({
        type: "setFilesViewed",
        filenames: filenames_0,
        viewed,
        versions
      });
    };
    $[40] = versions;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const setFilesViewed = t17;
  let t18;
  if ($[42] !== collapsedFiles || $[43] !== versions) {
    t18 = filename_5 => {
      if (!filename_5) {
        return;
      }
      dispatchUserChange({
        type: "setFilesCollapsed",
        filenames: [filename_5],
        collapsed: !collapsedFiles.has(filename_5),
        versions
      });
    };
    $[42] = collapsedFiles;
    $[43] = versions;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const toggleCollapsed = t18;
  let t19;
  if ($[45] !== versions) {
    t19 = (filename_6, collapsed) => {
      if (!filename_6) {
        return;
      }
      dispatchUserChange({
        type: "setFilesCollapsed",
        filenames: [filename_6],
        collapsed,
        versions
      });
    };
    $[45] = versions;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const setFileCollapsed = t19;
  let t20;
  if ($[47] !== versions) {
    t20 = (filenames_1, collapsed_0) => {
      if (filenames_1.length === 0) {
        return;
      }
      dispatchUserChange({
        type: "setFilesCollapsed",
        filenames: filenames_1,
        collapsed: collapsed_0,
        versions
      });
    };
    $[47] = versions;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const setFilesCollapsed = t20;
  let t21;
  if ($[49] !== changedSinceLastViewFiles || $[50] !== collapsedFiles || $[51] !== isCollapsed || $[52] !== isViewed || $[53] !== setFileCollapsed || $[54] !== setFilesCollapsed || $[55] !== setFilesViewed || $[56] !== toggleCollapsed || $[57] !== toggleViewed || $[58] !== viewedFiles) {
    t21 = {
      viewedFiles,
      changedSinceLastViewFiles,
      collapsedFiles,
      isViewed,
      isCollapsed,
      toggleViewed,
      setFilesViewed,
      toggleCollapsed,
      setFileCollapsed,
      setFilesCollapsed
    };
    $[49] = changedSinceLastViewFiles;
    $[50] = collapsedFiles;
    $[51] = isCollapsed;
    $[52] = isViewed;
    $[53] = setFileCollapsed;
    $[54] = setFilesCollapsed;
    $[55] = setFilesViewed;
    $[56] = toggleCollapsed;
    $[57] = toggleViewed;
    $[58] = viewedFiles;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  return t21;
}